import { DeeplinkAction } from 'components-machine';
import { noop } from 'lodash';
import React, { createContext, useCallback, useState } from 'react';
import { useICStateActions } from 'src/entrypoints/root/ICStateActionsContext';
import { webActionUrl } from 'src/utils/invokeWebAction';

type WebviewContextProps = {
  textSize?: 'M' | 'L';
  closeable?: boolean | undefined;
  canGoBack?: boolean | undefined;
  showProgressBar?: boolean;
  children: React.ReactNode;
  canGoToPreviousQuestion?: boolean;
};

type WebviewContextReturn = {
  textSize: 'M' | 'L';
  isKeyboard: boolean | null;
  setKeyboard: (value: boolean | null) => void;
  closeable: boolean;
  canGoBack: boolean;
  showProgressBar: boolean;
  canGoToPreviousQuestion: boolean;
  onClose: VoidFunction;
  invokeDeeplinkCtaHref: (ctaHref: string) => Promise<unknown>;
  invokeDeeplink: (deeplink: DeeplinkAction['deeplink']) => Promise<unknown>;
};

export const WebviewContext = createContext<WebviewContextReturn>({
  onClose: noop,
  textSize: 'M',
  isKeyboard: false,
  setKeyboard: noop,
  closeable: true,
  showProgressBar: true,
  canGoBack: false,
  canGoToPreviousQuestion: false,
  invokeDeeplink: () => Promise.resolve(),
  invokeDeeplinkCtaHref: () => Promise.resolve(),
});

const WebviewContextWrapper: React.FC<WebviewContextProps> = ({
  children,
  textSize = 'M',
  showProgressBar = true,
  closeable = true,
  canGoBack = false,
  canGoToPreviousQuestion = false,
}) => {
  const [isKeyboard, setKeyboard] = useState<boolean | null>(null);

  const { invokeDeeplink, onClose } = useICStateActions();

  const invokeDeeplinkCtaHref = useCallback(
    async (ctaHref: string) => {
      if (webActionUrl({ method: 'close' }) === ctaHref) {
        await invokeDeeplink({
          by: 'location',
          payload: { method: 'close' },
        });
        return;
      } else if (webActionUrl({ method: 'complete' }) === ctaHref) {
        await invokeDeeplink({
          by: 'location',
          payload: { method: 'complete' },
        });
        return;
      } else {
        await invokeDeeplink({
          by: 'location',
          payload: { method: 'deeplink', query: `?deeplink=${encodeURIComponent(ctaHref)}` },
        });
      }
    },
    [invokeDeeplink],
  );

  return (
    <WebviewContext.Provider
      value={{
        onClose,
        textSize,
        showProgressBar,
        closeable,
        canGoBack,
        isKeyboard,
        setKeyboard,
        canGoToPreviousQuestion,
        invokeDeeplink,
        invokeDeeplinkCtaHref,
      }}
    >
      {children}
    </WebviewContext.Provider>
  );
};

export default WebviewContextWrapper;

export const withWebviewContext = <T extends object>(Component: React.ComponentType<T>) => {
  return function ComponentWithWebviewContext(props: T) {
    return (
      <WebviewContextWrapper>
        <Component {...props} />
      </WebviewContextWrapper>
    );
  };
};
